import {
  Component,
  DestroyRef,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableComponent } from '@custom-components/general/table/table.component';
import { FIRMWARE_VERSIONS_TABLE_COLUMNS_CONFIG } from '@core/constants';
import { FirmwareModel } from '@core/models';
import { TagStateOf } from '@core/helpers';
import {
  FirmwareActions,
  selectFirmwareItems,
  selectIsLoading,
} from '@root/app/store/firmware.index';
import { ConfirmDeactivateFirmwareDialogComponent } from '../../components/confirm-deactivate-firmware-dialog/confirm-deactivate-firmware-dialog.component';

const BYTES_PER_MB = 1024 * 1024;

@Component({
  selector: 'xemex-firmware-versions-page',
  imports: [MatDialogModule, TableComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './firmware-versions-page.component.html',
})
export class FirmwareVersionsPageComponent {
  private readonly store = inject(Store);
  readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly columns = FIRMWARE_VERSIONS_TABLE_COLUMNS_CONFIG;

  protected readonly isLoading = signal(false);
  protected readonly firmwareItems = signal<FirmwareModel[]>([]);
  protected readonly totalCount = computed(() => this.firmwareItems().length);

  protected readonly tableData = computed(
    () =>
      this.firmwareItems().map(fw => {
        const activeKey = fw.isActive ? 'Active' : 'Inactive';
        return {
          ...fw,
          formattedSize:
            fw.sizeBytes != null ? `${(fw.sizeBytes / BYTES_PER_MB).toFixed(2)} MB` : '–',
          isActiveStatus: this.translate.instant(
            fw.isActive ? 'firmware.versions.status.active' : 'firmware.versions.status.inactive',
          ),
          isActiveState: TagStateOf.firmwareActive(activeKey),
        };
      }) as unknown as Record<string, unknown>[],
  );

  constructor() {
    this.store.dispatch(FirmwareActions.getFirmwareVersions({}));

    this.store
      .select(selectIsLoading)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(loading => this.isLoading.set(loading));

    this.store
      .select(selectFirmwareItems)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => this.firmwareItems.set(items));
  }

  protected onRowClick(row: Record<string, unknown>): void {
    const firmware = row as unknown as FirmwareModel;
    if (!firmware.isActive) return;

    const dialogRef = this.dialog.open(ConfirmDeactivateFirmwareDialogComponent, {
      width: '500px',
      data: {
        version: firmware.version,
        filename: firmware.filename ?? '–',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.store.dispatch(
            FirmwareActions.deactivateFirmware({ firmwareId: firmware.firmwareId }),
          );
        }
      });
  }
}
